// Components
import "../components/forms/select-many/selectMany";
import { CCCSelectMany } from "../components/forms/select-many/selectMany";

// Interfaces
import Tag from "../core/interfaces/Tag";
import SelectItem from "../core/interfaces/SelectItem";
import CategoryModel from "../models/CategoryModel";
import UserModel from "../models/UserModel";
import User from "../core/interfaces/User";
import { getMode } from "../core/utilities";

export default class CategoryTagsController {
  $originalTagsFld: HTMLElement;
  $newTagsFld: CCCSelectMany;

  // Current user info.
  private userInfo: User;
  // Category dropdown has event listener.
  private categoryDropdownSetterAdded = false;

  /**
   * Replace the original tags field in "New Discussion" and "Edit Discussion" pages.
   */
  public replaceCategoryTagsField(): void {
    console.debug(
      "Category Tags - Displaying dropdown list with category tags..."
    );
    const $categoryTags = document.getElementById("categoryTags");
    if ($categoryTags) {
      $categoryTags.remove();
    }
    this.$originalTagsFld = document.querySelector(".Form-Tags.P");
    // Hide original tags field.
    if (this.$originalTagsFld) {
      this.$originalTagsFld.style.display = "none";
      // Create and display new tags field.
      this.$newTagsFld = <CCCSelectMany>(
        document.createElement("ccc-select-many")
      );
      this.$originalTagsFld.parentElement.insertBefore(
        this.$newTagsFld,
        this.$originalTagsFld
      );
      //Fetch user data before updating category tags
      this.getCurrentUserInfo();
    } else {
      console.error(`Category Tags - Original tags field not found`);
    }
  }

  /*
    Fetch cached info from current user and store it on variable userInfo
  */
  private getCurrentUserInfo(): void {
    UserModel.getCurrentUserCached()
      .then((userInfo) => {
        this.userInfo = userInfo;
      })
      .catch((error) => {
        console.error(
          "Category Tags - Error while fetching cached user info before loading tags:",
          error
        );
      })
      .finally(() => {
        // Update category tags and display field.
        this.initCategoryTagsField();
      });
  }

  private setMaxSelectedTabs(): void {
    CategoryModel.getCategoryFromJsonPageCached()
      .then((pageMetadata) => {
        const urlCode = pageMetadata?.Category?.UrlCode?.toLowerCase() || "";
        const isAnnouncementType = [
          "announcements",
          "applications-readiness",
        ].some((keyword) => urlCode.includes(keyword));

        const numOfPreviouslySelectedTags =
          this.$newTagsFld?.getValue().length || 0;
        const maxSelectedTags = isAnnouncementType
          ? "6"
          : numOfPreviouslySelectedTags > 3
          ? numOfPreviouslySelectedTags.toString()
          : "3";

        this.$newTagsFld.setAttribute("max-selected-items", maxSelectedTags);
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Gets category tags and shows tags field.
   */
  private initCategoryTagsField(): void {
    //Validate if current user is admin or moderator
    const isUserAdminOrMod =
      this.userInfo &&
      (this.userInfo.roles.includes("ADMINISTRATOR") ||
        this.userInfo.roles.includes("MODERATOR"));
    this.$newTagsFld.id = "categoryTags";
    this.$newTagsFld.setAttribute("label", "<strong>Tags</strong>");

    //If user is admin no minimum tagsare required, else, 1 tag minimum is set on select many
    isUserAdminOrMod
      ? console.debug("Category Tags - No minimum tags are required")
      : this.$newTagsFld.setAttribute("min-selected-items", "1");

    this.$newTagsFld.setAttribute("internal-loading", "");
    this.$newTagsFld.setAttribute("disabled", "");
    CategoryModel.getCategoryFromJsonPageCached()
      .then((pageMetadata) => {
        if (pageMetadata.Category) {
          console.debug("Category Tags - It's a category page.");
          return CategoryModel.getCategoryTags();
        } else {
          console.debug("Category Tags - It not a category page.");
          const categoryId = document.getElementById(
            "Form_CategoryID"
          ) as HTMLInputElement | null;
          if (categoryId?.value) {
            return CategoryModel.getCategoryTags(+categoryId.value);
          } else {
            if (!this.categoryDropdownSetterAdded) {
              const $categorySelected =
                document.getElementById("Form_CategoryID");
              if ($categorySelected) {
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const self = this;
                const { get, set } = Object.getOwnPropertyDescriptor(
                  HTMLInputElement.prototype,
                  "value"
                );
                Object.defineProperty($categorySelected, "value", {
                  get() {
                    return get.call(this);
                  },
                  set(newVal) {
                    self.replaceCategoryTagsField();
                    return set.call(this, newVal);
                  },
                });
                this.categoryDropdownSetterAdded = true;
              }
            }
            throw "Category Tags: Category not selected.";
          }
        }
      })
      .then((tags) => {
        console.debug("Category Tags - Tags:", tags);
        if (tags && tags.length > 0) {
          this.updateTags(tags);
          this.addEvents();
          this.addTagSentOnUrl(tags);
          this.validateSubmitButton();
        } else {
          this.$newTagsFld.remove();
          throw "Category Tags - Category doesn't have tags.";
        }
      })
      .catch((error) => {
        console.error(error);
        this.$newTagsFld.remove();
      })
      .finally(() => {
        this.setMaxSelectedTabs();
        this.$newTagsFld.removeAttribute("internal-loading");
        this.$newTagsFld.removeAttribute("disabled");
      });
  }

  /**
   * Disables submit button if there are no tags selected.
   */
  private validateSubmitButton(): void {
    const buttonsDiv = document.querySelector(".Buttons");
    const buttons = buttonsDiv.querySelectorAll(".Button");
    const isUserAdminOrMod =
      this.userInfo &&
      (this.userInfo.roles.includes("ADMINISTRATOR") ||
        this.userInfo.roles.includes("MODERATOR"));
    buttons.forEach((button: HTMLElement) => {
      if (
        button.tagName.toLowerCase() === "input" &&
        (<HTMLInputElement>button).value.toLowerCase() !== "cancel"
      ) {
        if (
          (this.$newTagsFld && this.$newTagsFld.getValue().length > 0) ||
          isUserAdminOrMod
        ) {
          // Enable submit buttons.
          button.removeAttribute("disabled");
        } else {
          // Disable submit buttons.
          button.setAttribute("disabled", "");
        }
      }
    });
  }

  /**
   * Updates both original and new tags fields with category tags.
   * @param {Array<Tags>} tags
   */
  private updateTags(tags: Array<Tag>): void {
    // Update original tags field.
    const $availableTagsList: HTMLElement = this.$originalTagsFld.querySelector(
      ".Hidden.AvailableTags"
    );
    if ($availableTagsList) {
      $availableTagsList.innerHTML = "";
      tags.forEach((tag: Tag) => {
        const anchor = document.createElement("a");
        anchor.setAttribute("href", "/dev/post/discussion/sales#");
        anchor.classList.add("AvailableTag");
        anchor.setAttribute("data-name", tag.urlCode);
        anchor.setAttribute("data-id", tag.id.toString());
        anchor.innerHTML = tag.name;
        $availableTagsList.append(anchor);
      });
      $availableTagsList.style.display = "block";
    }
    // Update new tags field.
    this.$newTagsFld.setKeyState("options", tags);
    // Initialize new tags field (Edit page).
    const $tagsInput = this.$originalTagsFld.querySelector("#Form_Tags");
    try {
      const tagsSelected = JSON.parse($tagsInput.getAttribute("data-tags"));
      const tagsIds = Object.keys(tagsSelected);
      tagsIds.forEach((tagId) => {
        const tag = tags.find((tag: Tag) => tag.id === parseInt(tagId, 10));
        if (tag) {
          this.$newTagsFld.addItem(tag);
        }
      });
    } catch (error) {
      console.error(
        "Category Tags - Bad JSON formatting on original tags field"
      );
    }
  }

  /**
   * Adds extra behavior when user selects or removes a tag in new tags field.
   */
  private addEvents(): void {
    // Add onAddItem event.
    this.$newTagsFld.onAddItem((itemToAdd: SelectItem) => {
      this.validateSubmitButton();
      const $originalItem: HTMLElement = this.$originalTagsFld.querySelector(
        `[data-id="${itemToAdd.id}"]`
      );
      if ($originalItem && getMode() !== "development") {
        $originalItem.click();
      }
    });
    // Add onRemoveItem event.
    this.$newTagsFld.onRemoveItem((itemToRemove: SelectItem) => {
      this.validateSubmitButton();
      const ul = this.$originalTagsFld.querySelector(".token-input-list");
      ul.childNodes.forEach((e: HTMLElement) => {
        const $tagNameElement = e.querySelector("p");
        if (
          $tagNameElement &&
          $tagNameElement.innerHTML === itemToRemove.name
        ) {
          const $removeButton = e.querySelector("span");
          if ($removeButton) {
            $removeButton.click();
          }
        }
      });
    });
  }

  /**
   * Add tag sent on query parameters to category tags field.
   * @param {Array<Tag>} tags
   */
  private addTagSentOnUrl(tags: Array<Tag>) {
    const urlParams = new URLSearchParams(window.location.search);
    const tagSent = urlParams.get("tag");
    if (tagSent) {
      const tag = tags.find((tag: Tag) => tag.urlCode === tagSent);
      if (tag) {
        this.$newTagsFld.addItem(tag);
      }
    }
  }

  /**
   * Hides the parent tag if the discussion has tags.
   */
  public hideParentTag(): void {
    console.debug("Category Tags - Hiding parent tags...");
    const tagsContainer = document.querySelector(".InlineTags.Meta ul");
    if (tagsContainer) {
      const anchors = tagsContainer.querySelectorAll('[class*="Tag_cat"]');
      if (anchors.length > 0) {
        anchors.forEach((anchor) => {
          tagsContainer.removeChild(anchor.parentElement);
        });
      }
    }
  }
}
